import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, ArrowLeft, Users, Crown, BookOpen, Gamepad2, Trophy, MessageSquare, Plus, Trash2, Megaphone } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

export default function Admin() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [lessons, setLessons] = useState([]);
  const [ann, setAnn] = useState({ title: "", body: "" });
  const [form, setForm] = useState({ category_key: "money", title: "", type: "quick_read", skill_branch: "financial", xp: 20, coins: 10 });

  const loadLessons = () => api.get("/admin/lessons").then((r) => setLessons(r.data)).catch(() => {});

  useEffect(() => {
    if (!user?.is_admin) return;
    api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/admin/users").then((r) => setUsers(r.data)).catch(() => {});
    loadLessons();
  }, [user]);

  if (!user?.is_admin) return <div className="text-center py-20"><p className="text-muted-foreground">Admin access required.</p></div>;
  if (!stats) return <div className="grid place-items-center py-32"><Loader2 className="animate-spin text-indigo-400" size={32} /></div>;

  const createLesson = async () => {
    if (!form.title) return toast.error("Title required");
    try {
      await api.post("/admin/lessons", { ...form, content: { body: "New lesson content.", keyPoints: [], minutes: 3, items: [], cards: [], questions: [] } });
      toast.success("Lesson created");
      setForm({ ...form, title: "" });
      loadLessons();
    } catch { toast.error("Failed to create"); }
  };

  const delLesson = async (id) => {
    try { await api.delete(`/admin/lessons/${id}`); loadLessons(); toast.success("Deleted"); } catch { toast.error("Failed"); }
  };

  const sendAnn = async () => {
    if (!ann.title) return toast.error("Title required");
    try { await api.post("/admin/announcements", ann); toast.success("Announcement sent"); setAnn({ title: "", body: "" }); } catch { toast.error("Failed"); }
  };

  const statCards = [
    { l: "Users", v: stats.total_users, i: Users, c: "text-indigo-400" },
    { l: "Premium", v: stats.premium_users, i: Crown, c: "text-yellow-400" },
    { l: "Lessons Done", v: stats.lessons_completed, i: BookOpen, c: "text-emerald-400" },
    { l: "Sims Done", v: stats.simulations_completed, i: Gamepad2, c: "text-cyan-400" },
    { l: "Challenges", v: stats.challenges_completed, i: Trophy, c: "text-orange-400" },
    { l: "Coach Msgs", v: stats.coach_messages, i: MessageSquare, c: "text-violet-400" },
  ];

  return (
    <div>
      <button onClick={() => navigate("/app")} className="flex items-center gap-1.5 text-muted-foreground hover:text-foreground text-sm font-semibold mb-4"><ArrowLeft size={16} /> Home</button>
      <h1 className="font-display font-extrabold text-3xl tracking-tight mb-1">Admin Dashboard</h1>
      <p className="text-muted-foreground mb-6">Manage LyfePilot content, users and engagement.</p>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-7">
        {statCards.map((s) => (
          <div key={s.l} className="glass rounded-2xl p-4">
            <s.i size={20} className={`${s.c} mb-2`} />
            <div className="text-2xl font-display font-extrabold">{s.v}</div>
            <div className="text-[11px] uppercase tracking-wider text-muted-foreground font-bold">{s.l}</div>
          </div>
        ))}
      </div>

      <Tabs defaultValue="content">
        <TabsList className="bg-white/5 rounded-full p-1 mb-5">
          <TabsTrigger value="content" data-testid="admin-tab-content" className="rounded-full data-[state=active]:bg-indigo-600 data-[state=active]:text-white">Content</TabsTrigger>
          <TabsTrigger value="users" data-testid="admin-tab-users" className="rounded-full data-[state=active]:bg-indigo-600 data-[state=active]:text-white">Users</TabsTrigger>
          <TabsTrigger value="announce" data-testid="admin-tab-announce" className="rounded-full data-[state=active]:bg-indigo-600 data-[state=active]:text-white">Announce</TabsTrigger>
        </TabsList>

        <TabsContent value="content">
          <div className="glass rounded-3xl p-5 mb-5">
            <h3 className="font-display font-bold text-sm mb-3 flex items-center gap-2"><Plus size={16} className="text-indigo-400" /> Create Lesson</h3>
            <div className="grid sm:grid-cols-2 gap-3">
              <Input data-testid="admin-lesson-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Lesson title" className="rounded-2xl bg-white/5 border-white/10" />
              <Select value={form.category_key} onValueChange={(v) => setForm({ ...form, category_key: v })}>
                <SelectTrigger data-testid="admin-lesson-cat" className="rounded-2xl bg-white/5 border-white/10"><SelectValue /></SelectTrigger>
                <SelectContent>{["money", "career", "housing", "health", "mental_health", "relationships", "law", "technology", "productivity", "goals"].map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
              <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                <SelectTrigger data-testid="admin-lesson-type" className="rounded-2xl bg-white/5 border-white/10"><SelectValue /></SelectTrigger>
                <SelectContent>{["quick_read", "quiz", "flashcards", "checklist"].map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
              <Input type="number" value={form.xp} onChange={(e) => setForm({ ...form, xp: parseInt(e.target.value) || 0 })} placeholder="XP" className="rounded-2xl bg-white/5 border-white/10" />
            </div>
            <Button data-testid="admin-create-lesson-btn" onClick={createLesson} className="mt-3 rounded-2xl bg-indigo-600 hover:bg-indigo-500 text-white font-semibold">Create Lesson</Button>
          </div>
          <div className="space-y-2 max-h-[420px] overflow-y-auto no-scrollbar">
            {lessons.map((l) => (
              <div key={l.id} className="flex items-center gap-3 glass rounded-2xl p-3">
                <div className="flex-1 min-w-0"><span className="text-sm font-semibold truncate block">{l.title}</span><span className="text-[11px] text-muted-foreground">{l.category_key} · {l.type} · {l.xp} XP</span></div>
                <button data-testid={`admin-del-${l.id}`} onClick={() => delLesson(l.id)} className="h-8 w-8 grid place-items-center rounded-full hover:bg-rose-500/10 text-rose-400"><Trash2 size={15} /></button>
              </div>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="users">
          <div className="space-y-2 max-h-[520px] overflow-y-auto no-scrollbar">
            {users.map((u) => (
              <div key={u.user_id} className="flex items-center gap-3 glass rounded-2xl p-4">
                <div className="h-9 w-9 rounded-full bg-gradient-to-br from-indigo-500 to-violet-600 grid place-items-center text-white font-bold text-sm shrink-0">{u.name?.[0]?.toUpperCase() || "?"}</div>
                <div className="flex-1 min-w-0"><span className="text-sm font-semibold">{u.name}</span><span className="text-[11px] text-muted-foreground block truncate">{u.email || "guest"}</span></div>
                <span className="text-xs text-muted-foreground">Lv {u.level}</span>
                <span className="text-xs font-bold text-yellow-400">{u.xp} XP</span>
                {u.is_premium && <Crown size={14} className="text-yellow-400" />}
              </div>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="announce">
          <div className="glass rounded-3xl p-5">
            <h3 className="font-display font-bold text-sm mb-3 flex items-center gap-2"><Megaphone size={16} className="text-indigo-400" /> Send Announcement</h3>
            <Input data-testid="ann-title" value={ann.title} onChange={(e) => setAnn({ ...ann, title: e.target.value })} placeholder="Title" className="rounded-2xl bg-white/5 border-white/10 mb-3" />
            <Textarea data-testid="ann-body" value={ann.body} onChange={(e) => setAnn({ ...ann, body: e.target.value })} placeholder="Message to all users…" className="rounded-2xl bg-white/5 border-white/10 min-h-24 mb-3" />
            <Button data-testid="send-ann-btn" onClick={sendAnn} className="rounded-2xl bg-indigo-600 hover:bg-indigo-500 text-white font-semibold">Broadcast</Button>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
